/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { AlertTriangle, CheckCircle2, RefreshCw, X, XCircle } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { Button } from "../../../../shared/components/ui/button";
import type { FFprobeMetadata, VideoAsset } from "../types";
import { getStatusBadge } from "../utils/videoAssetHelpers";
import { ThumbnailScrubber } from "./ThumbnailScrubber";

interface EditVideoAssetModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly titleName: string;
  readonly asset: VideoAsset;
  readonly onSave: () => void;
  readonly onRevalidate?: (assetId: string) => Promise<void>;
}

/**
 * Format duration in seconds to human-readable format
 */
function formatDuration(seconds: number | null): string {
  if (!seconds) return "—";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  }
  return `${minutes}m ${secs}s`;
}

/**
 * Format bitrate to human-readable format (e.g., "5.2 Mbps")
 */
function formatBitrate(kbps: number | undefined): string {
  if (!kbps) return "—";
  if (kbps >= 1000) {
    return `${(kbps / 1000).toFixed(1)} Mbps`;
  }
  return `${kbps} kbps`;
}

/**
 * Format file size to human-readable format
 */
function formatFileSize(bytes: number | undefined): string {
  if (!bytes) return "—";
  if (bytes >= 1073741824) {
    return `${(bytes / 1073741824).toFixed(2)} GB`;
  }
  if (bytes >= 1048576) {
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }
  return `${(bytes / 1024).toFixed(0)} KB`;
}

/**
 * Format channel layout for display
 */
function formatChannels(channels: number | undefined, layout?: string): string {
  if (!channels) return "—";
  if (layout) return layout;
  if (channels === 1) return "Mono";
  if (channels === 2) return "Stereo";
  if (channels === 6) return "5.1";
  if (channels === 8) return "7.1";
  return `${channels} channels`;
}

/**
 * FFprobe metadata display section
 */
function FFprobeSection({ ffprobe }: { readonly ffprobe: FFprobeMetadata }): ReactElement {
  return (
    <div className="space-y-4">
      {/* Container & General Info */}
      <div>
        <h4 className="text-sm font-medium text-white mb-3">Container</h4>
        <div className="grid grid-cols-6 gap-3 text-xs">
          <div className="">
            <div className="text-neutral-500">Format</div>
            <div className="text-white font-medium">{ffprobe.container.toUpperCase()}</div>
          </div>
          <div className="">
            <div className="text-neutral-500">Duration</div>
            <div className="text-white font-medium">{formatDuration(Math.round(ffprobe.duration))}</div>
          </div>
          <div className="">
            <div className="text-neutral-500">File Size</div>
            <div className="text-white font-medium">{formatFileSize(ffprobe.fileSize)}</div>
          </div>
        </div>
      </div>

      {/* Video Stream */}
      {ffprobe.video && (
        <div>
          <h4 className="text-sm font-medium text-white mb-3">Video Stream</h4>
          <div className="grid grid-cols-6 gap-3 text-xs">
            <div className="">
              <div className="text-neutral-500">Codec</div>
              <div className="text-white font-medium">
                {ffprobe.video.codec.toUpperCase()}
                {ffprobe.video.profile && <span className="text-neutral-400 ml-1">({ffprobe.video.profile})</span>}
              </div>
            </div>
            <div className="">
              <div className="text-neutral-500">Resolution</div>
              <div className="text-white font-medium">
                {ffprobe.video.width}×{ffprobe.video.height}
                {ffprobe.video.aspectRatio && (
                  <span className="text-neutral-400 ml-1">({ffprobe.video.aspectRatio})</span>
                )}
              </div>
            </div>
            <div className="">
              <div className="text-neutral-500">Frame Rate</div>
              <div className="text-white font-medium">{ffprobe.video.frameRate.toFixed(2)} fps</div>
            </div>
            <div className="">
              <div className="text-neutral-500">Bitrate</div>
              <div className="text-white font-medium">{formatBitrate(ffprobe.video.bitrate)}</div>
            </div>
            <div className="">
              <div className="text-neutral-500">HDR</div>
              <div className={`font-medium ${ffprobe.isHDR ? "text-lumi-accent" : "text-neutral-400"}`}>
                {ffprobe.isHDR ? "Yes" : "No"}
              </div>
            </div>
            <div className="">
              <div className="text-neutral-500">Interlaced</div>
              <div className={`font-medium ${ffprobe.video.isInterlaced ? "text-yellow-400" : "text-neutral-400"}`}>
                {ffprobe.video.isInterlaced ? "Yes" : "No"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audio Stream */}
      {ffprobe.audio && (
        <div>
          <h4 className="text-sm font-medium text-white mb-3">Audio Stream</h4>
          <div className="grid grid-cols-6 gap-3 text-xs">
            <div className="">
              <div className="text-neutral-500">Codec</div>
              <div className="text-white font-medium">{ffprobe.audio.codec.toUpperCase()}</div>
            </div>
            <div className="">
              <div className="text-neutral-500">Channels</div>
              <div className="text-white font-medium">
                {formatChannels(ffprobe.audio.channels, ffprobe.audio.channelLayout)}
              </div>
            </div>
            <div className="">
              <div className="text-neutral-500">Sample Rate</div>
              <div className="text-white font-medium">{(ffprobe.audio.sampleRate / 1000).toFixed(1)} kHz</div>
            </div>
            <div className="">
              <div className="text-neutral-500">Bitrate</div>
              <div className="text-white font-medium">{formatBitrate(ffprobe.audio.bitrate)}</div>
            </div>
            {ffprobe.audio.language && (
              <div className="">
                <div className="text-neutral-500">Language</div>
                <div className="text-white font-medium">{ffprobe.audio.language.toUpperCase()}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analysis Metadata */}
      <div className="text-[10px] text-neutral-500 pt-2">
        Analyzed {new Date(ffprobe.analyzedAt).toLocaleString()} • Analysis took {ffprobe.analysisDurationMs}ms
      </div>
    </div>
  );
}

/**
 * Subtitles content section - read-only display
 * Full management is now in SubtitleManagementDialog accessed from the row
 */
function SubtitlesContent({ asset }: Readonly<{ asset: VideoAsset }>): ReactElement {
  if (asset.subtitles.length === 0) {
    return (
      <div className="text-xs text-neutral-500 italic py-4 text-center">
        No subtitles uploaded. Click the subtitles cell in the table to add subtitles.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {asset.subtitles.map((subtitle) => (
        <div
          key={subtitle.id}
          className="flex items-center justify-between p-3 bg-neutral-900/50 border border-neutral-800 rounded"
        >
          <div className="flex items-center gap-3 text-xs">
            <span className="font-medium text-white">{subtitle.lang.toUpperCase()}</span>
            <span className="text-neutral-500">•</span>
            <span className="text-neutral-400">{subtitle.format}</span>
            {subtitle.forced && (
              <>
                <span className="text-neutral-500">•</span>
                <span className="text-lumi-accent">Forced</span>
              </>
            )}
          </div>
        </div>
      ))}
      <p className="text-xs text-neutral-500 mt-2">Click the subtitles cell in the table to manage subtitles.</p>
    </div>
  );
}

/**
 * Modal for viewing video asset details and managing subtitles
 */
export function EditVideoAssetModal({
  open,
  onClose,
  titleName,
  asset,
  onRevalidate,
}: Readonly<EditVideoAssetModalProps>): ReactElement | null {
  const [isClosing, setIsClosing] = useState(false);
  const [isRevalidating, setIsRevalidating] = useState(false);

  useEffect(() => {
    if (open) {
      setIsClosing(false);
    }
  }, [open]);

  if (!open && !isClosing) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  const handleRevalidate = async () => {
    if (!onRevalidate) return;
    setIsRevalidating(true);
    try {
      await onRevalidate(asset.id);
    } finally {
      setIsRevalidating(false);
    }
  };

  const isVisible = open && !isClosing;
  const assetKind = asset.kind === "FEATURE" ? "Feature" : "Trailer";

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
    >
      {/* Backdrop overlay */}
      <button
        type="button"
        className={`absolute inset-0 bg-black/60 cursor-default ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
        onClick={handleClose}
        aria-label="Close modal"
      />

      {/* Modal content */}
      <div
        className={`relative bg-neutral-800/20 backdrop-blur-md border border-neutral-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col ${isVisible ? "animate-in zoom-in-95 fade-in duration-200" : "animate-out zoom-out-95 fade-out duration-200"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <div>
            <h2 className="text-xl font-semibold text-white">{assetKind} Video</h2>
            <p className="text-sm text-neutral-400 mt-1">{titleName}</p>
          </div>
          <button type="button" onClick={handleClose} className="text-neutral-400 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
          {/* Video Information */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-white mb-3">Video Information</h3>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="flex items-center">
                  <span className="text-neutral-500">Status:</span>
                  <span
                    className={`ml-2 font-medium inline-flex items-center gap-1 ${getStatusBadge(asset.encodingStatus).color}`}
                  >
                    {getStatusBadge(asset.encodingStatus).icon}
                    {getStatusBadge(asset.encodingStatus).label}
                  </span>
                </div>
                <div>
                  <span className="text-neutral-500">Duration:</span>
                  <span className="ml-2 text-neutral-300">{formatDuration(asset.durationSec)}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-neutral-500">Stream ID:</span>
                  <span className="ml-2 text-neutral-300 font-mono text-[10px]">{asset.streamVideoId || "—"}</span>
                </div>
              </div>
              {asset.encodingError && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
                  <strong>Error:</strong> {asset.encodingError}
                </div>
              )}
            </div>

            {/* FFprobe Analysis Section */}
            {asset.ffprobe && (
              <div className="pt-4 border-t border-neutral-800">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-white">Source Video Analysis</h3>
                  {onRevalidate && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void handleRevalidate()}
                      disabled={isRevalidating}
                      className="text-xs"
                    >
                      <RefreshCw className={`h-3 w-3 mr-1 ${isRevalidating ? "animate-spin" : ""}`} />
                      {isRevalidating ? "Analyzing..." : "Re-analyze"}
                    </Button>
                  )}
                </div>
                <FFprobeSection ffprobe={asset.ffprobe} />
              </div>
            )}

            {/* No FFprobe data available */}
            {!asset.ffprobe && onRevalidate && (
              <div className="pt-4 border-t border-neutral-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-white">Source Video Analysis</h3>
                    <p className="text-xs text-neutral-500 mt-1">No analysis data available for this video</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void handleRevalidate()}
                    disabled={isRevalidating}
                    className="text-xs"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${isRevalidating ? "animate-spin" : ""}`} />
                    {isRevalidating ? "Analyzing..." : "Analyze Video"}
                  </Button>
                </div>
              </div>
            )}

            {/* Thumbnail Scrubber Section */}
            {asset.thumbnailVttUrl && (
              <div className="pt-4 border-t border-neutral-800">
                <h3 className="text-sm font-medium text-white mb-3">Timeline Thumbnails</h3>
                <ThumbnailScrubber vttUrl={asset.thumbnailVttUrl} duration={asset.durationSec || 0} />
              </div>
            )}

            {/* Subtitles Section */}
            <div className="pt-4 border-t border-neutral-800">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-white">Subtitles ({asset.subtitles.length})</h3>
              </div>

              <SubtitlesContent asset={asset} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-neutral-800">
          {asset.ffprobe?.validation && (
            <div className="text-xs">
              {asset.ffprobe.validation.valid && asset.ffprobe.validation.warnings.length === 0 && (
                <div className="flex items-center gap-2 font-medium text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Video meets all quality requirements</span>
                </div>
              )}
              {asset.ffprobe.validation.valid && asset.ffprobe.validation.warnings.length > 0 && (
                <>
                  <div className="flex items-center gap-2 font-medium text-yellow-400">
                    <AlertTriangle className="h-4 w-4" />
                    <span>Quality Notes:</span>
                  </div>
                  <ul className="mt-2 space-y-1 text-yellow-300">
                    {asset.ffprobe.validation.warnings.map((warning) => (
                      <li key={`warn-${warning.slice(0, 20)}`} className="flex items-start gap-2">
                        <span className="text-yellow-500">•</span>
                        {warning}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {!asset.ffprobe.validation.valid && (
                <>
                  <div className="flex items-center gap-2 font-medium text-red-400">
                    <XCircle className="h-4 w-4" />
                    <span>Validation Errors:</span>
                  </div>
                  <ul className="mt-2 space-y-1 text-red-300">
                    {asset.ffprobe.validation.errors.map((error) => (
                      <li key={`err-${error.slice(0, 20)}`} className="flex items-start gap-2">
                        <span className="text-red-500">•</span>
                        {error}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
          <Button type="button" onClick={handleClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
